import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppServices } from "../../hook/services";
import { useWhiteLabelInfo } from "../../context/whitelabel";

const ArticleList = () => {
  const [WhiteLabel] = useWhiteLabelInfo();
  const { categoryId } = useParams();
  const AppService = useAppServices();

  const backUrl = `/`;

  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const getCategory = async () => {
    try {
      const { response } = await AppService.knowledgebase_category.filter({
        query: `_id=${categoryId}`,
      });
      if (response?.data) {
        setCategory(response.data);
      } else {
        setCategory(null);
      }
    } catch (error) {
      console.error(error);
      setCategory(null);
    } finally {
    }
  };

  const getArticles = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.knowledgebase_article.get_location_articles({
        query: `category=${categoryId}&companyId=${WhiteLabel?.companyId}`,
      });
      if (response) {
        setArticles(response.data);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error(error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategory();
    getArticles();
  }, []);

  const formatDate = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const headerBg = WhiteLabel?.bgColor || "#6c757d";
  const headerTextColor = WhiteLabel?.textColor || "white";

  if (loading) {
    return (
      <div className="kb-page">
        <header className="kb-hero kb-hero--with-back" style={{ background: headerBg }}>
          <div className="container kb-hero-inner">
            <div className="kb-skel kb-skel--light rounded" style={{ height: 28, width: 80 }} />
            <div className="text-center mt-3">
              <div className="kb-skel kb-skel--light rounded mx-auto" style={{ height: 56, width: 56, borderRadius: 16 }} />
              <div className="kb-skel kb-skel--light rounded mx-auto mt-3" style={{ height: 22, width: 220 }} />
              <div className="kb-skel kb-skel--light rounded mx-auto mt-2" style={{ height: 12, width: 280 }} />
            </div>
          </div>
        </header>
        <div className="container kb-toolbar">
          <div className="kb-skel rounded mx-auto" style={{ height: 44, width: "100%", maxWidth: 560 }} />
        </div>
        <div className="container kb-section">
          <div className="kb-skel rounded mb-3" style={{ height: 20, width: 220 }} />
          <div className="d-flex flex-column gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="kb-article-card-skel">
                <div className="d-flex align-items-start gap-3">
                  <div className="kb-skel rounded" style={{ width: 36, height: 36, flexShrink: 0 }} />
                  <div className="flex-grow-1">
                    <div className="kb-skel rounded" style={{ height: 12, width: "60%" }} />
                    <div className="kb-skel rounded mt-2" style={{ height: 10, width: "85%" }} />
                    <div className="kb-skel rounded mt-2" style={{ height: 10, width: "30%" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <KbStyles />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="kb-page">
        <div className="container py-5">
          <div className="kb-empty d-flex flex-column align-items-center mx-auto text-center" style={{ maxWidth: 460 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            </div>
            <h5 className="kb-empty-title">Category not found</h5>
            <p className="kb-empty-text mb-3">
              The category you're looking for doesn't exist or may have been removed.
            </p>
            <Link to={backUrl} className="kb-btn kb-btn--ghost">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Go back
            </Link>
          </div>
        </div>
        <KbStyles />
      </div>
    );
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredArticles = !normalizedQuery
    ? articles
    : articles.filter((a) => {
        const name = (a.name || "").toLowerCase();
        const desc = (a.shortDescription || "").toLowerCase();
        return name.includes(normalizedQuery) || desc.includes(normalizedQuery);
      });

  return (
    <div className="kb-page">
      {/* Hero */}
      <header className="kb-hero kb-hero--with-back" style={{ background: headerBg }}>
        <div className="container kb-hero-inner">
          <div className="d-flex justify-content-start mb-3">
            <Link to={backUrl} className="kb-hero-back" style={{ color: headerTextColor }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
            </Link>
          </div>

          <div className="text-center">
            <span className="kb-hero-icon d-inline-flex align-items-center justify-content-center mb-3" style={{ color: headerTextColor }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <h1 className="kb-hero-title" style={{ color: headerTextColor }}>
              {category.name}
            </h1>
            {category.description && (
              <p className="kb-hero-subtitle" style={{ color: headerTextColor }}>
                {category.description}
              </p>
            )}
            <span className="kb-hero-badge">
              {normalizedQuery
                ? `${filteredArticles.length} of ${articles.length} articles`
                : `${articles.length} ${articles.length === 1 ? "article" : "articles"} available`}
            </span>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="container kb-toolbar">
        <div className="kb-search-wrap position-relative mx-auto w-100" style={{ maxWidth: 560 }}>
          <span className="kb-search-icon d-flex align-items-center justify-content-center" aria-hidden>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            type="text"
            className="kb-search-input"
            placeholder={`Search within ${category.name}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="kb-search-clear"
              onClick={() => setSearchQuery("")}
              title="Clear search"
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {normalizedQuery && (
          <div className="text-center kb-result-line mt-2">
            {filteredArticles.length === 0
              ? `No articles match "${searchQuery}"`
              : `Showing results for "${searchQuery}"`}
          </div>
        )}
      </div>

      {/* List */}
      <div className="container kb-section">
        <h4 className="kb-section-title-left">
          {normalizedQuery ? "Search results" : `All articles in ${category.name}`}
        </h4>

        {filteredArticles.length === 0 ? (
          <div className="kb-empty d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 480 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h6 className="kb-empty-title">
              {normalizedQuery ? "No matching articles" : "No articles found in this category"}
            </h6>
            {normalizedQuery && (
              <p className="kb-empty-text mb-0">
                Try a different search term or{" "}
                <button
                  type="button"
                  className="kb-link-btn"
                  onClick={() => setSearchQuery("")}
                >
                  clear the search
                </button>
                .
              </p>
            )}
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {filteredArticles.map((a) => (
              <Link
                key={a._id}
                to={`./${a._id}`}
                className="kb-article-card"
              >
                <div className="d-flex align-items-start gap-3">
                  <span className="kb-article-card-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="13" y2="17" />
                    </svg>
                  </span>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <h6 className="kb-article-card-title mb-1">{a.name}</h6>
                    {a.shortDescription && (
                      <p className="kb-article-card-desc mb-1">{a.shortDescription}</p>
                    )}
                    <div className="kb-article-card-meta d-inline-flex align-items-center">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      Updated {formatDate(a.updatedAt)}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="kb-article-arrow flex-shrink-0">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 28px 16px 56px;
    }
    .kb-hero--with-back { padding-top: 18px; }
    .kb-hero-inner { max-width: 760px; margin: 0 auto; }
    .kb-hero-back {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 8px;
      text-decoration: none;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.18);
      transition: background 0.15s ease, transform 0.15s ease;
    }
    .kb-hero-back:hover {
      background: rgba(255, 255, 255, 0.26);
      text-decoration: none;
    }
    .kb-hero-back:hover svg { transform: translateX(-2px); }
    .kb-hero-back svg { transition: transform 0.15s ease; }
    .kb-hero-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    .kb-hero-title {
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin: 0 0 8px;
    }
    .kb-hero-subtitle {
      font-size: 0.9375rem;
      opacity: 0.92;
      margin: 0 0 16px;
    }
    .kb-hero-badge {
      display: inline-flex;
      align-items: center;
      padding: 5px 12px;
      font-size: 0.75rem;
      font-weight: 500;
      color: #1f2937;
      background: rgba(255, 255, 255, 0.92);
      border-radius: 999px;
      letter-spacing: 0.02em;
    }

    /* Toolbar */
    .kb-toolbar { margin-top: 28px; padding: 0 16px; }

    /* Search */
    .kb-search-wrap { max-width: 560px; }
    .kb-search-icon {
      position: absolute;
      left: 14px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
    }
    .kb-search-input {
      width: 100%;
      height: 44px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 0 42px 0 40px;
      background: #fff;
      font-size: 0.875rem;
      color: #1f2937;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    .kb-search-input::placeholder { color: #9ca3af; }
    .kb-search-input:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
    }
    .kb-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-search-clear:hover { background: #f3f4f6; color: #1f2937; }
    .kb-result-line {
      font-size: 0.8125rem;
      color: #6b7280;
    }
    .kb-link-btn {
      background: transparent;
      border: 0;
      padding: 0;
      color: #4f46e5;
      cursor: pointer;
      font: inherit;
    }
    .kb-link-btn:hover { color: #4338ca; text-decoration: underline; }

    /* Section */
    .kb-section { padding: 32px 16px 64px; }
    .kb-section-title-left {
      font-size: 1.0625rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
      margin: 0 0 18px;
    }

    /* Article card */
    .kb-article-card {
      display: block;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 14px 16px;
      text-decoration: none;
      color: inherit;
      transition: border-color 0.15s ease, box-shadow 0.2s ease, transform 0.15s ease;
    }
    .kb-article-card:hover {
      border-color: #c7d2fe;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 6px 14px rgba(79, 70, 229, 0.08);
      transform: translateY(-1px);
      text-decoration: none;
      color: inherit;
    }
    .kb-article-card-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: #eef2ff;
      color: #4f46e5;
      transition: background 0.15s ease;
    }
    .kb-article-card:hover .kb-article-card-icon { background: #e0e7ff; }
    .kb-article-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-article-card-desc {
      font-size: 0.8125rem;
      color: #6b7280;
      line-height: 1.5;
    }
    .kb-article-card-meta {
      font-size: 0.6875rem;
      font-weight: 500;
      color: #9ca3af;
      letter-spacing: 0.02em;
    }
    .kb-article-arrow {
      color: #cbd5e1;
      align-self: center;
      transition: color 0.15s ease, transform 0.15s ease;
    }
    .kb-article-card:hover .kb-article-arrow {
      color: #4f46e5;
      transform: translateX(2px);
    }

    /* Buttons */
    .kb-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 14px;
      border-radius: 8px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .kb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      text-decoration: none;
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
      padding: 40px 24px;
    }
    .kb-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }

    /* Skeleton */
    .kb-article-card-skel {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 14px 16px;
    }
    .kb-skel {
      background: linear-gradient(90deg, #eef2f7 0%, #f3f4f6 50%, #eef2f7 100%);
      background-size: 200% 100%;
      animation: kb-shimmer 1.4s ease-in-out infinite;
    }
    .kb-skel--light {
      background: linear-gradient(90deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.18) 100%);
      background-size: 200% 100%;
    }
    @keyframes kb-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
  `}</style>
);

export default ArticleList;
